import { useSimpleTableContext } from "../../../contexts/SimpleTableContext";
import type { SimpleTableContextValue } from "../../../contexts/SimpleTableContext";
import { useFilterContext } from "@/contexts/FilterContext";
import { useMemo } from "react";
import React from "react";
import type { BaseEntity } from "../../../types";

type Ctx<TData extends BaseEntity> = SimpleTableContextValue<TData>;

interface ContextExtractionResult<TData extends BaseEntity> {
  config: Ctx<TData>["config"];
  hooks: Ctx<TData>["hooks"];
  state: Pick<
    Ctx<TData>["state"],
    | "selectedItems"
    | "setSelectedItems"
    | "sortColumns"
    | "sortField"
    | "sortDirection"
    | "draggedColumn"
    | "columnWidths"
    | "effectiveCurrentPage"
    | "effectivePageSize"
    | "effectiveSetCurrentPage"
    | "effectiveSetPageSize"
    | "effectiveAdvancedFilters"
    | "showFloatingBulkActions"
  >;
  callbacks: Ctx<TData>["callbacks"];
  refs: Ctx<TData>["refs"];
  auth: Ctx<TData>["auth"];
  utils: Ctx<TData>["utils"];
  groupingContext: Ctx<TData>["groupingContext"];
  groupableFields: Ctx<TData>["groupableFields"];
  openModal: Ctx<TData>["openModal"];
  visibleFields: Ctx<TData>["visibleFields"];
  processedData: TData[];
  paginatedData: TData[];
  totalPages: number;
  onInlineEdit: Ctx<TData>["onInlineEdit"];
  onEdit: Ctx<TData>["onEdit"];
  onView: Ctx<TData>["onView"];
  onDelete: Ctx<TData>["onDelete"];
  onOpenDetailModal: Ctx<TData>["onOpenDetailModal"];
  onBulkAction: Ctx<TData>["onBulkAction"];
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  autoScrollEnabled?: boolean;
  propTotalItems: number;
  propTotalPages: number;
  propOnPageChange?: (page: number) => void;
  error: Ctx<TData>["error"];
  hideColumnHeaders: boolean;
  hideTableBody: boolean;
  data: TData[];
  filterContext: ReturnType<typeof useFilterContext<TData>>;
}

/**
 * Extracts and organizes all context values from SimpleTableContext and FilterContext
 * Provides a structured interface to access context data without excessive destructuring
 *
 * Performance optimization:
 * - Wraps return value in useMemo to prevent unnecessary re-renders of child components
 * - Only recalculates when context values actually change
 * - Includes debug logging for performance monitoring
 */
export function useSimpleTableContextExtraction<TData extends BaseEntity>(): ContextExtractionResult<TData> {
  const context = useSimpleTableContext<TData>();
  const filterContext = useFilterContext<TData>();
  const recalcCountRef = React.useRef(0);

  // Memoize the result object to prevent child components from re-rendering
  // when context values haven't actually changed
  const result = useMemo((): ContextExtractionResult<TData> => {
    recalcCountRef.current++;
    if (process.env.NODE_ENV === "development") {
      console.debug("[SimpleTable] Context extraction recalculated", {
        recalcCount: recalcCountRef.current,
        timestamp: new Date().toISOString(),
      });
    }

    return {
      // Configuration
      config: context.config,
      hooks: context.hooks,

      // State
      state: {
        selectedItems: context.state.selectedItems,
        setSelectedItems: context.state.setSelectedItems,
        sortColumns: context.state.sortColumns,
        sortField: context.state.sortField,
        sortDirection: context.state.sortDirection,
        draggedColumn: context.state.draggedColumn,
        columnWidths: context.state.columnWidths,
        effectiveCurrentPage: context.state.effectiveCurrentPage,
        effectivePageSize: context.state.effectivePageSize,
        effectiveSetCurrentPage: context.state.effectiveSetCurrentPage,
        effectiveSetPageSize: context.state.effectiveSetPageSize,
        effectiveAdvancedFilters: context.state.effectiveAdvancedFilters,
        showFloatingBulkActions: context.state.showFloatingBulkActions,
      },

      // Callbacks
      callbacks: context.callbacks,

      // Refs
      refs: context.refs,

      // Auth
      auth: context.auth,

      // Utils
      utils: context.utils,

      // Grouping
      groupingContext: context.groupingContext,
      groupableFields: context.groupableFields,

      // Modal and data
      openModal: context.openModal,
      visibleFields: context.visibleFields,
      processedData: context.processedData,
      paginatedData: context.paginatedData,
      totalPages: context.totalPages,

      // Event handlers
      onInlineEdit: context.onInlineEdit,
      onEdit: context.onEdit,
      onView: context.onView,
      onDelete: context.onDelete,
      onOpenDetailModal: context.onOpenDetailModal,
      onBulkAction: context.onBulkAction,

      // Infinite scroll
      loadMore: context.loadMore,
      hasNextPage: context.hasNextPage,
      isFetchingNextPage: context.isFetchingNextPage,
      autoScrollEnabled: context.autoScrollEnabled,

      // Pagination props
      propTotalItems: context.propTotalItems ?? 0,
      propTotalPages: context.propTotalPages ?? 0,
      propOnPageChange: context.propOnPageChange,

      // Error
      error: context.error,

      // Hide column headers flag
      hideColumnHeaders: context.hideColumnHeaders ?? false,

      // Hide table body flag
      hideTableBody: context.hideTableBody ?? false,

      // Data
      data: context.data,

      // Filter context
      filterContext,
    };
  }, [
    // Configuration
    context.config,
    context.hooks,
    // State
    context.state.selectedItems,
    context.state.setSelectedItems,
    context.state.sortColumns,
    context.state.sortField,
    context.state.sortDirection,
    context.state.draggedColumn,
    context.state.columnWidths,
    context.state.effectiveCurrentPage,
    context.state.effectivePageSize,
    context.state.effectiveSetCurrentPage,
    context.state.effectiveSetPageSize,
    context.state.effectiveAdvancedFilters,
    context.state.showFloatingBulkActions,
    // Callbacks
    context.callbacks,
    // Refs
    context.refs,
    // Auth
    context.auth,
    // Utils
    context.utils,
    // Grouping
    context.groupingContext,
    context.groupableFields,
    // Modal and data
    context.openModal,
    context.visibleFields,
    context.processedData,
    context.paginatedData,
    context.totalPages,
    // Event handlers
    context.onInlineEdit,
    context.onEdit,
    context.onView,
    context.onDelete,
    context.onOpenDetailModal,
    context.onBulkAction,
    // Infinite scroll
    context.loadMore,
    context.hasNextPage,
    context.isFetchingNextPage,
    context.autoScrollEnabled,
    // Pagination props
    context.propTotalItems,
    context.propTotalPages,
    context.propOnPageChange,
    // Error
    context.error,
    // Hide column headers flag
    context.hideColumnHeaders,
    // Hide table body flag
    context.hideTableBody,
    // Data
    context.data,
    // Filter context
    filterContext,
  ]);

  return result;
}
